// Что показать системным уведомлением — чистая функция без React и без таймеров (T-047).

import type { FeedMessage } from '../api/alertFeedApi';
import { instant } from './mergeFeed';

/**
 * Старше этого по времени СЕРВЕРА — не показывать: «осталось 20 минут», пришедшее через
 * два часа (планшет был без сети), только сбивает. То же окно, что `CATCH_UP` на сервере.
 */
export const NOTIFY_FRESHNESS_MS = 30 * 60_000;

export type NotifyInput = {
  /** Галочка устройства включена, оболочка умеет, сессия своя (не саппорт). */
  readonly allowed: boolean;
  readonly location: string;
  /** Сообщения точки, новые первыми. */
  readonly messages: readonly FeedMessage[];
  /** До какого сообщения уведомления уже показаны; `null` — ещё ни разу. */
  readonly mark: string | null;
  readonly serverTime: string;
};

export type NotifyShow = {
  readonly title: string;
  readonly body: string;
  readonly tag: string;
};

export type NotifyPlan = {
  /** Новая отметка «показано»; `null` — не двигать. */
  readonly mark: string | null;
  readonly show: NotifyShow | null;
  /** Сколько свежих сообщений в сводке — для текста «Новых предупреждений: N». */
  readonly count: number;
};

const NOTHING: NotifyPlan = { mark: null, show: null, count: 0 };

/**
 * Решение по порядку:
 * 1. нельзя (галочки нет, оболочка не умеет, саппорт-сессия) — ничего;
 * 2. отметки нет (первый запуск, только что включили) — отметка на самое свежее, без показа:
 *    историю пачкой не вываливаем;
 * 3. кандидаты — новее отметки и не старше 30 минут по времени сервера;
 * 4. отметка сдвигается на самое свежее, даже если кандидатов нет;
 * 5. один — тост с его текстом, несколько — один сводный: пачки тостов нет.
 */
export function notifyPlan(input: NotifyInput): NotifyPlan {
  if (!input.allowed) return NOTHING;
  const newest = input.messages[0];
  if (input.mark === null) {
    return { mark: newest?.created_at ?? input.serverTime, show: null, count: 0 };
  }
  if (newest === undefined) return NOTHING;
  const after = instant(input.mark);
  const unseen = input.messages.filter((message) => instant(message.created_at) > after);
  if (unseen.length === 0) return NOTHING;
  const now = instant(input.serverTime);
  const fresh = unseen.filter(
    (message) => now - instant(message.created_at) <= NOTIFY_FRESHNESS_MS,
  );
  const mark = newest.created_at;
  const [first] = fresh;
  if (first === undefined) return { mark, show: null, count: 0 };
  if (fresh.length === 1) {
    return { mark, show: { title: first.title, body: first.summary, tag: first.id }, count: 1 };
  }
  return {
    mark,
    show: { title: first.title, body: '', tag: `restomarker-alerts-${input.location}` },
    count: fresh.length,
  };
}
